/**
 * Modal para configurar los requisitos de una clase progresiva para un club
 */

import { useState, useEffect } from "react";
import { Button, Input, Badge } from "@/components/ui";
import {
  X,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  Users,
  Settings,
} from "lucide-react";
import {
  useClubClassConfig,
  useUpsertClubClassConfig,
} from "@/hooks/useClubClassConfig";
import {
  useClassProgressByClubAndClass,
  useSyncRequirementsToExisting,
} from "@/hooks/useProgress";
import type { ClubClassRequirement } from "@/types";

interface ClassRequirementConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  clubId: string;
  classId: string;
  className: string;
}

interface RequirementInput {
  id: string;
  name: string;
  description: string;
  order: number;
}

export default function ClassRequirementConfigModal({
  isOpen,
  onClose,
  clubId,
  classId,
  className,
}: ClassRequirementConfigModalProps) {
  const [requirements, setRequirements] = useState<RequirementInput[]>([]);
  const [newRequirement, setNewRequirement] = useState("");

  const { data: existingConfig, isLoading } = useClubClassConfig(
    clubId,
    classId,
  );
  const { data: existingProgresses } = useClassProgressByClubAndClass(
    clubId,
    classId,
  );
  const upsertMutation = useUpsertClubClassConfig();
  const syncMutation = useSyncRequirementsToExisting();

  const inProgressCount = existingProgresses?.length || 0;

  // Cargar requisitos existentes
  useEffect(() => {
    if (existingConfig?.requirements) {
      setRequirements(
        existingConfig.requirements.map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description || "",
          order: r.order,
        })),
      );
    }
  }, [existingConfig]);

  const handleAddRequirement = () => {
    if (!newRequirement.trim()) return;

    const newReq: RequirementInput = {
      id: `temp_${Date.now()}`,
      name: newRequirement.trim(),
      description: "",
      order: requirements.length + 1,
    };

    setRequirements([...requirements, newReq]);
    setNewRequirement("");
  };

  const handleRemoveRequirement = (id: string) => {
    setRequirements(requirements.filter((r) => r.id !== id));
  };

  const handleUpdateRequirement = (id: string, name: string) => {
    setRequirements(
      requirements.map((r) => (r.id === id ? { ...r, name } : r)),
    );
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newReqs = [...requirements];
    [newReqs[index - 1], newReqs[index]] = [newReqs[index], newReqs[index - 1]];
    // Actualizar order
    newReqs.forEach((r, i) => (r.order = i + 1));
    setRequirements(newReqs);
  };

  const handleMoveDown = (index: number) => {
    if (index === requirements.length - 1) return;
    const newReqs = [...requirements];
    [newReqs[index], newReqs[index + 1]] = [newReqs[index + 1], newReqs[index]];
    // Actualizar order
    newReqs.forEach((r, i) => (r.order = i + 1));
    setRequirements(newReqs);
  };

  const handleSave = async () => {
    const reqs: Omit<ClubClassRequirement, "id">[] = requirements.map(
      (r, index) => ({
        name: r.name,
        description: r.description,
        order: index + 1,
      }),
    );

    // Guardar la configuración
    await upsertMutation.mutateAsync({
      clubId,
      classId,
      className,
      requirements: reqs,
    });

    // Si hay progresos existentes, sincronizar los requisitos
    if (inProgressCount > 0 && requirements.length > 0) {
      const reqsWithIds = requirements.map((r, index) => ({
        id: r.id.startsWith("temp_") ? `req_${Date.now()}_${index}` : r.id,
        name: r.name,
        description: r.description,
        order: index + 1,
      }));

      await syncMutation.mutateAsync({
        clubId,
        classId,
        requirements: reqsWithIds,
      });
    }

    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddRequirement();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Configurar Requisitos
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Clase:{" "}
                <span className="font-medium text-green-600">{className}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Info */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 border-2 border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">
                      Define los requisitos que los miembros deben cumplir
                    </p>
                    <p className="mt-1">
                      Estos requisitos se usarán cuando inicies el progreso de
                      un miembro en esta clase. Puedes añadir, editar o eliminar
                      requisitos en cualquier momento.
                    </p>
                  </div>
                </div>
              </div>

              {/* Aviso de progresos existentes */}
              {inProgressCount > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 border-2 border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">
                        {inProgressCount} miembro
                        {inProgressCount !== 1 ? "s" : ""} en progreso
                      </p>
                      <p className="mt-1">
                        Al guardar, los requisitos se actualizarán
                        automáticamente para los miembros que ya tienen esta
                        clase en progreso. Se preservará el estado de completado
                        para requisitos con el mismo nombre.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Añadir requisito */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Añadir nuevo requisito
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ej: Memorizar el Voto del Conquistador"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddRequirement}
                    disabled={!newRequirement.trim()}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir
                  </Button>
                </div>
              </div>

              {/* Lista de requisitos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">
                    Requisitos ({requirements.length})
                  </h3>
                  {requirements.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      Arrastra para reordenar
                    </Badge>
                  )}
                </div>

                {requirements.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-white">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <CheckCircle className="w-8 h-8 text-gray-500" />
                    </div>
                    <p className="text-gray-500 font-medium">
                      No hay requisitos configurados
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Añade requisitos usando el campo de arriba
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {requirements.map((req, index) => (
                      <div
                        key={req.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border-2 border-gray-100 group"
                      >
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors duration-200"
                            title="Mover arriba"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleMoveDown(index)}
                            disabled={index === requirements.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors duration-200"
                            title="Mover abajo"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>

                        <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />

                        <span className="text-sm font-medium text-gray-500 w-6">
                          {index + 1}.
                        </span>

                        <input
                          type="text"
                          value={req.name}
                          onChange={(e) =>
                            handleUpdateRequirement(req.id, e.target.value)
                          }
                          className="flex-1 bg-transparent border-0 focus:ring-0 text-sm text-gray-700 p-0"
                        />

                        <button
                          onClick={() => handleRemoveRequirement(req.id)}
                          className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={upsertMutation.isPending || syncMutation.isPending}
          >
            {upsertMutation.isPending || syncMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {syncMutation.isPending ? "Sincronizando..." : "Guardar Requisitos"}
            {inProgressCount > 0 &&
              !upsertMutation.isPending &&
              !syncMutation.isPending && (
                <span className="ml-1 text-xs opacity-75">
                  (+ actualizar {inProgressCount})
                </span>
              )}
          </Button>
        </div>
      </div>
    </div>
  );
}
